import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  mapSeniorCenterApplyPayloadToRegisterRequest,
  sanitizeSeniorCenterApplyPayload,
} from '../utils/senior-center-registration.mapper';

type IssueAuthTokenResponse = {
  userId: number;
  token: string;
};

@Injectable({
  providedIn: 'root',
})
export class StaffEnrollmentSubmitService {
  private readonly http = inject(HttpClient);
  private readonly userHost =
    (environment as { userHost?: string }).userHost ?? environment.host;
  private readonly programHost =
    (environment as { programHost?: string }).programHost ?? environment.host;

  submitSeniorCenterApplication(
    applyPayload: Record<string, unknown>,
    status: 'draft' | 'pending' | 're-submit'
  ): Observable<unknown> {
    const payloadWithLocation = this.appendApplicationId(
      this.appendStaffLocation(applyPayload)
    );
    // Continue / resume must use the existing application owner — never a file-upload
    // placeholder session user, and never register a second account for the same email.
    const existingUserId = this.resolveEnrollmentUserId();
    const register$ =
      existingUserId > 0
        ? of({ userId: existingUserId })
        : this.registerParticipant(payloadWithLocation);

    return register$.pipe(
      switchMap(({ userId }) => this.issueParticipantToken(userId)),
      switchMap(({ userId, token }) => {
        this.persistParticipantSession(userId, token);
        // Always push recipient phone onto users.phone; apply also syncs as a backstop.
        return this.updateParticipantProfile(payloadWithLocation, token).pipe(
          catchError(() => of(undefined)),
          switchMap(() => this.applyProgram(payloadWithLocation, status, token))
        );
      })
    );
  }

  private appendStaffLocation(payload: Record<string, unknown>): Record<string, unknown> {
    if (typeof sessionStorage === 'undefined') {
      return payload;
    }

    try {
      const locationRaw = sessionStorage.getItem('selectedLocation');
      if (!locationRaw) {
        return payload;
      }

      const location = JSON.parse(locationRaw) as { id?: string; locationName?: string };
      if (!location?.id?.trim()) {
        return payload;
      }

      return {
        ...payload,
        staff_location_id: location.id.trim(),
        staff_location_name: location.locationName?.trim() ?? '',
      };
    } catch {
      return payload;
    }
  }

  private registerParticipant(applyPayload: Record<string, unknown>): Observable<{ userId: number }> {
    const body = mapSeniorCenterApplyPayloadToRegisterRequest(applyPayload);
    return this.http
      .post<{ userId?: number; UserId?: number; message?: string }>(`${this.userHost}/api/auth/register`, body)
      .pipe(
        map((response) => {
          const userId = Number(response?.userId ?? response?.UserId);
          if (!Number.isFinite(userId) || userId <= 0) {
            throw new Error('User registration succeeded but user id was missing');
          }
          return { userId };
        })
      );
  }

  private updateParticipantProfile(
    applyPayload: Record<string, unknown>,
    token: string
  ): Observable<unknown> {
    const body = mapSeniorCenterApplyPayloadToRegisterRequest(applyPayload);
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
    return this.http.put(`${this.userHost}/api/auth/profile`, body, { headers });
  }

  private issueParticipantToken(userId: number): Observable<IssueAuthTokenResponse> {
    return this.http
      .post<IssueAuthTokenResponse>(`${this.userHost}/api/auth/internal/issue-auth-token`, { userId })
      .pipe(
        map((response) => {
          const token = (response?.token ?? (response as { Token?: string })?.Token)?.trim();
          if (!token) {
            throw new Error('Failed to issue participant auth token');
          }
          return { userId, token };
        })
      );
  }

  private applyProgram(
    applyPayload: Record<string, unknown>,
    status: 'draft' | 'pending' | 're-submit',
    token: string
  ): Observable<unknown> {
    const isDraft = status === 'draft';
    const payload = sanitizeSeniorCenterApplyPayload({
      ...applyPayload,
      status: isDraft ? 'draft' : 'pending',
      ...(isDraft ? {} : { immediate_approval: true }),
    });

    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });

    return this.http.post(`${this.programHost}/api/program/staticform/apply`, payload, { headers });
  }

  /**
   * Prefer the real application owner (set when Continue loads the draft).
   * Only fall back to the upload placeholder when this is a brand-new enrollment
   * that has not registered a participant yet.
   */
  private resolveEnrollmentUserId(): number {
    const owned = this.readPositiveUserId(
      sessionStorage.getItem('staff_enrollment_user_id')
    );
    if (owned > 0) {
      return owned;
    }
    return this.readPositiveUserId(
      sessionStorage.getItem('staff_enrollment_session_user_id')
    );
  }

  private readPositiveUserId(raw: string | null): number {
    if (!raw) {
      return 0;
    }
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }

  /** Soft-reopen / Continue: keep updates on the same user_program row. */
  private appendApplicationId(
    payload: Record<string, unknown>
  ): Record<string, unknown> {
    const existing = String(
      payload['application_id'] ?? payload['applicationId'] ?? ''
    ).trim();
    if (existing) {
      return payload;
    }

    let applicationId = '';
    try {
      applicationId =
        new URLSearchParams(window.location.search).get('applicationId')?.trim() ||
        '';
    } catch {
      applicationId = '';
    }

    if (!applicationId) {
      return payload;
    }

    return {
      ...payload,
      application_id: applicationId,
    };
  }

  private persistParticipantSession(userId: number, token: string): void {
    sessionStorage.setItem('staff_enrollment_user_id', String(userId));
    sessionStorage.removeItem('staff_enrollment_session_user_id');
    sessionStorage.setItem('participant_auth_token', token);
    localStorage.setItem('user_id', String(userId));
  }
}
